import { camelCase } from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Telemetry } from '@videoblocks/kafka-rest-client';

import { AIPromptInputRef } from '../AIPromptInput';
import { UseCase } from '../utils/constants';
import { getAutoOpenState } from '../utils/stateUtils';
import { VideoMetadata, VideoSelection } from '../utils/videoUtils';

/** Key used in overrides map when user has not selected a suggestion (custom text per use case). */
const NO_SUGGESTION_KEY = '__noSuggestion__';

interface VideoEditorState {
  // Video metadata and source
  videoSrc: string | null;
  videoMetadata: VideoMetadata | null;
  isLoadingMetadata: boolean;
  error: string | null;

  // Playback state
  isPlaying: boolean;
  currentTime: number;

  // Selection state
  selection: VideoSelection | null;

  // UI state
  selectedUseCase: string | null;
  selectedSuggestion: string | null;
  // Saves previous user input:
  // { [useCaseId]: { [suggestion]: promptText } }
  promptOverridesByUseCaseAndSuggestion: Record<string, Record<string, string>>;
  defaultPlaceholder: string;
  promptText: string;
  referenceImage: string | null;
  showCreditConfirmation: boolean;
  isGeneratingTaskId: boolean;
  showPurchaseCredits: boolean;

  // Refs
  promptInputRef: React.RefObject<AIPromptInputRef>;
}

interface VideoEditorActions {
  // Video metadata actions
  setVideoSrc: (src: string | null) => void;
  setVideoMetadata: (metadata: VideoMetadata | null) => void;
  setIsLoadingMetadata: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Playback actions
  setIsPlaying: (playing: boolean) => void;
  setCurrentTime: (time: number) => void;
  handlePlayPause: () => void;
  /** Imperative seek for trim/move scrubbing (avoids React render round-trip) */
  seekTo: (time: number) => void;
  /** Register video element's seek implementation. Returns cleanup. */
  registerSeek: (fn: (time: number) => void) => () => void;

  // Selection actions
  setSelection: (selection: VideoSelection | null) => void;
  handleSelectionChange: (selection: VideoSelection) => void;
  handleTimeUpdate: (time: number) => void;

  // UI actions
  setSelectedUseCase: (useCase: string | null) => void;
  setDefaultPlaceholder: (prompt: string) => void;
  setPromptText: (prompt: string) => void;
  setReferenceImage: (image: string | null) => void;
  setShowCreditConfirmation: (show: boolean) => void;
  setIsGeneratingTaskId: (generating: boolean) => void;
  handleUseCaseSelect: (id: string, useCases: UseCase[]) => void;
  handleSuggestionSelect: (suggestion: string) => void;
  resetPromptOverrides: () => void;
  handleReferenceImageChange: (image: string | null) => void;
  setShowPurchaseCredits: (show: boolean) => void;
}

interface VideoEditorContextValue
  extends VideoEditorState,
    VideoEditorActions {}

const VideoEditorContext = createContext<VideoEditorContextValue | null>(null);

interface VideoEditorProviderProps {
  children: React.ReactNode;
  initialUseCase?: UseCase;
}

export const VideoEditorProvider: React.FC<VideoEditorProviderProps> = ({
  children,
  initialUseCase,
}) => {
  // Try to restore state from sessionStorage (SSR-safe, single operation)
  const restoredState = useMemo(() => {
    const { shouldOpen, storedState } = getAutoOpenState();
    return shouldOpen ? storedState : null;
  }, []);

  // Video metadata and source state
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const [videoMetadata, setVideoMetadata] = useState<VideoMetadata | null>(
    null
  );
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Playback state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  // Selection state
  const [selection, setSelection] = useState<VideoSelection | null>(
    restoredState?.selection || null
  );

  // UI state
  const [selectedUseCase, setSelectedUseCase] = useState<string | null>(
    restoredState?.selectedUseCase || initialUseCase?.id || null
  );
  const [selectedSuggestion, setSelectedSuggestion] = useState<string | null>(
    null
  );
  const [
    promptOverridesByUseCaseAndSuggestion,
    setPromptOverridesByUseCaseAndSuggestion,
  ] = useState<Record<string, Record<string, string>>>({});
  const [defaultPlaceholder, setDefaultPlaceholder] = useState(
    restoredState?.defaultPlaceholder || initialUseCase?.defaultText || ''
  );
  const [promptText, setPromptText] = useState(restoredState?.promptText || '');
  const [referenceImage, setReferenceImage] = useState<string | null>(null);
  const [showCreditConfirmation, setShowCreditConfirmation] = useState(false);
  const [showPurchaseCredits, setShowPurchaseCredits] = useState(false);
  const [isGeneratingTaskId, setIsGeneratingTaskId] = useState(false);

  // Refs
  const promptInputRef = useRef<AIPromptInputRef>(null);
  const seekToRef = useRef<((time: number) => void) | null>(null);
  // using refs prevents unnecessary re-renders by securing the object reference between renders.
  const promptOverridesRef = useRef(promptOverridesByUseCaseAndSuggestion);
  promptOverridesRef.current = promptOverridesByUseCaseAndSuggestion;

  // Imperative seek for trim/move scrubbing (like Maker) - avoids React render round-trip
  const seekTo = useCallback((time: number) => {
    seekToRef.current?.(time);
  }, []);

  const registerSeek = useCallback((fn: (time: number) => void) => {
    seekToRef.current = fn;
    return () => {
      seekToRef.current = null;
    };
  }, []);

  // Actions - memoized to prevent unnecessary re-renders
  const handlePlayPause = useCallback(() => {
    setIsPlaying((prev) => !prev);
  }, []);

  const handleSelectionChange = useCallback((newSelection: VideoSelection) => {
    setSelection(newSelection);
  }, []);

  const handleTimeUpdate = useCallback((time: number) => {
    setCurrentTime(time);
  }, []);

  const updatePromptOverrides = useCallback(
    (
      useCaseId: string,
      selectedSuggestion: string,
      currentValue: string
    ): void => {
      if (useCaseId) {
        const keyToSave = selectedSuggestion ?? NO_SUGGESTION_KEY;
        setPromptOverridesByUseCaseAndSuggestion((prev) => {
          const nextState = { ...prev };
          const useCaseOverrides = nextState[useCaseId] ?? {};
          // if the user has input text, save it by the use-case id + suggestion. If there is no suggestion, we use the NO_SUGGESTION_KEY.
          if (currentValue) {
            nextState[useCaseId] = {
              ...useCaseOverrides,
              [keyToSave]: currentValue,
            };
          } else {
            // removes the saved text if the user has cleared the input, so we can use the default suggestion text again.
            const { [keyToSave]: _, ...rest } = useCaseOverrides;
            if (Object.keys(rest).length === 0) {
              delete nextState[useCaseId];
            } else {
              nextState[useCaseId] = rest;
            }
          }
          return nextState;
        });
      }
    },
    [setPromptOverridesByUseCaseAndSuggestion]
  );

  const handleUseCaseSelect = useCallback(
    (id: string, useCases: UseCase[]) => {
      const previousUseCaseId = selectedUseCase;
      const previousSuggestion = selectedSuggestion;
      const currentValue =
        promptInputRef.current?.getPromptValue()?.trim() ?? '';

      updatePromptOverrides(
        previousUseCaseId,
        previousSuggestion,
        currentValue
      );

      setSelectedUseCase(id);
      setSelectedSuggestion(null);

      const useCase = useCases.find((uc) => uc.id === id);
      if (useCase) {
        setDefaultPlaceholder(useCase.defaultText);
        promptInputRef.current?.setPromptPlaceholderValue(useCase.defaultText);
        const noSuggestionOverrideText =
          promptOverridesRef.current[id]?.[NO_SUGGESTION_KEY];
        promptInputRef.current?.setPromptValue(noSuggestionOverrideText ?? '');
        Telemetry.increment(`ai.contentEdit.useCase.${camelCase(useCase.id)}`);
      }
    },
    [selectedUseCase, selectedSuggestion]
  );

  const handleSuggestionSelect = useCallback(
    (suggestion: string) => {
      const useCaseId = selectedUseCase;
      const currentValue =
        promptInputRef.current?.getPromptValue()?.trim() ?? '';

      updatePromptOverrides(useCaseId, selectedSuggestion, currentValue);

      setSelectedSuggestion(suggestion);
      const stored =
        useCaseId && promptOverridesRef.current[useCaseId]?.[suggestion];
      const textToSet = stored ?? suggestion;
      promptInputRef.current?.setPromptValue(textToSet);
    },
    [selectedUseCase, selectedSuggestion]
  );

  const resetPromptOverrides = useCallback(() => {
    setPromptOverridesByUseCaseAndSuggestion({});
    setSelectedSuggestion(null);
    promptInputRef.current?.setPromptValue('');
  }, []);

  const handleReferenceImageChange = useCallback((image: string | null) => {
    setReferenceImage(image);
  }, []);

  const contextValue = useMemo<VideoEditorContextValue>(
    () => ({
      // State
      videoSrc,
      videoMetadata,
      isLoadingMetadata,
      error,
      isPlaying,
      currentTime,
      selection,
      selectedUseCase,
      selectedSuggestion,
      promptOverridesByUseCaseAndSuggestion,
      defaultPlaceholder,
      promptText,
      referenceImage,
      showCreditConfirmation,
      isGeneratingTaskId,
      promptInputRef,
      showPurchaseCredits,

      // Actions
      setVideoSrc,
      setVideoMetadata,
      setIsLoadingMetadata,
      setError,
      setIsPlaying,
      setCurrentTime,
      handlePlayPause,
      seekTo,
      registerSeek,
      setSelection,
      handleSelectionChange,
      handleTimeUpdate,
      setSelectedUseCase,
      setDefaultPlaceholder,
      setPromptText,
      setReferenceImage,
      setShowCreditConfirmation,
      setShowPurchaseCredits,
      setIsGeneratingTaskId,
      handleUseCaseSelect,
      handleSuggestionSelect,
      resetPromptOverrides,
      handleReferenceImageChange,
    }),
    [
      videoSrc,
      videoMetadata,
      isLoadingMetadata,
      error,
      isPlaying,
      currentTime,
      selection,
      selectedUseCase,
      selectedSuggestion,
      promptOverridesByUseCaseAndSuggestion,
      defaultPlaceholder,
      promptText,
      referenceImage,
      showCreditConfirmation,
      showPurchaseCredits,
      isGeneratingTaskId,
      handlePlayPause,
      seekTo,
      registerSeek,
      handleSelectionChange,
      handleTimeUpdate,
      handleUseCaseSelect,
      handleSuggestionSelect,
      resetPromptOverrides,
      handleReferenceImageChange,
    ]
  );

  return (
    <VideoEditorContext.Provider value={contextValue}>
      {children}
    </VideoEditorContext.Provider>
  );
};

// Custom hooks for consuming context
export const useVideoEditorContext = (): VideoEditorContextValue => {
  const context = useContext(VideoEditorContext);
  if (!context) {
    throw new Error(
      'useVideoEditorContext must be used within a VideoEditorProvider'
    );
  }
  return context;
};

export const useVideoSelection = () => {
  const context = useVideoEditorContext();
  return {
    selection: context.selection,
    setSelection: context.setSelection,
    handleSelectionChange: context.handleSelectionChange,
  };
};

export const useVideoPlayback = () => {
  const context = useVideoEditorContext();
  return {
    isPlaying: context.isPlaying,
    currentTime: context.currentTime,
    setIsPlaying: context.setIsPlaying,
    setCurrentTime: context.setCurrentTime,
    handlePlayPause: context.handlePlayPause,
    handleTimeUpdate: context.handleTimeUpdate,
    seekTo: context.seekTo,
    registerSeek: context.registerSeek,
  };
};

export const useVideoMetadata = () => {
  const context = useVideoEditorContext();
  return {
    videoSrc: context.videoSrc,
    videoMetadata: context.videoMetadata,
    isLoadingMetadata: context.isLoadingMetadata,
    error: context.error,
    setVideoSrc: context.setVideoSrc,
    setVideoMetadata: context.setVideoMetadata,
    setIsLoadingMetadata: context.setIsLoadingMetadata,
    setError: context.setError,
  };
};

export const useVideoUI = () => {
  const context = useVideoEditorContext();
  return {
    selectedUseCase: context.selectedUseCase,
    selectedSuggestion: context.selectedSuggestion,
    promptOverridesByUseCaseAndSuggestion:
      context.promptOverridesByUseCaseAndSuggestion,
    defaultPlaceholder: context.defaultPlaceholder,
    promptText: context.promptText,
    referenceImage: context.referenceImage,
    showCreditConfirmation: context.showCreditConfirmation,
    isGeneratingTaskId: context.isGeneratingTaskId,
    promptInputRef: context.promptInputRef,
    setSelectedUseCase: context.setSelectedUseCase,
    setDefaultPlaceholder: context.setDefaultPlaceholder,
    setPromptText: context.setPromptText,
    setReferenceImage: context.setReferenceImage,
    setShowCreditConfirmation: context.setShowCreditConfirmation,
    setIsGeneratingTaskId: context.setIsGeneratingTaskId,
    handleUseCaseSelect: context.handleUseCaseSelect,
    handleSuggestionSelect: context.handleSuggestionSelect,
    resetPromptOverrides: context.resetPromptOverrides,
    handleReferenceImageChange: context.handleReferenceImageChange,
    showPurchaseCredits: context.showPurchaseCredits,
    setShowPurchaseCredits: context.setShowPurchaseCredits,
  };
};
